import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

const STATUS_COLOR = {
  open: { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b' },
  active: { bg: 'rgba(0,213,91,0.1)', text: '#00d55b' },
  resolved: { bg: 'rgba(0,213,91,0.08)', text: '#00d55b' },
  missed: { bg: 'rgba(255,68,68,0.1)', text: '#ff4444' },
  offline: { bg: '#1a1a1a', text: '#555' }
};

export default function BusinessChats() {
  const [chats, setChats] = useState([]);
  const [filter, setFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tab, setTab] = useState('messages');
  const [loading, setLoading] = useState(false);

  const loadChats = (f = filter) => {
    setLoading(true);
    axios.get(`/api/chats?status=${f}&limit=50`)
      .then(r => setChats(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadChats(); }, [filter]);

  const selectChat = (chat) => {
    setSelected(chat);
    setTab('messages');
    axios.get(`/api/chats/${chat.id}/messages`).then(r => setMessages(r.data.data || [])).catch(() => {});
  };

  const resolveChat = async (chatId) => {
    await axios.put(`/api/chats/${chatId}/resolve`);
    toast.success('Chat resolved');
    loadChats();
    if (selected?.id === chatId) setSelected(s => ({ ...s, status: 'resolved' }));
  };

  const counts = { open: 0, active: 0, resolved: 0, missed: 0 };
  chats.forEach(c => { if (counts[c.status] !== undefined) counts[c.status]++; });

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

        {/* Left: chat list */}
        <div style={{ width: 300, borderRight: '1px solid #1e1e1e', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e1e1e' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>All Chats</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {['open', 'active', 'resolved', 'missed'].map(s => (
                <button key={s} onClick={() => setFilter(s)}
                  style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${filter === s ? STATUS_COLOR[s]?.text : '#1e1e1e'}`, background: filter === s ? STATUS_COLOR[s]?.bg : 'transparent', color: filter === s ? STATUS_COLOR[s]?.text : '#555', fontSize: 11, cursor: 'pointer', textTransform: 'capitalize', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  {s}
                  {counts[s] > 0 && <span style={{ background: STATUS_COLOR[s]?.text, color: '#000', fontSize: 9, padding: '0 5px', borderRadius: 8, fontWeight: 700 }}>{counts[s]}</span>}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#333', fontSize: 13 }}>Loading...</div>
            ) : chats.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#333', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
                No {filter} chats
              </div>
            ) : chats.map(chat => (
              <div key={chat.id} onClick={() => selectChat(chat)}
                style={{ padding: '12px 14px', borderBottom: '1px solid #0d0d0d', cursor: 'pointer', background: selected?.id === chat.id ? 'rgba(0,213,91,0.04)' : 'transparent', borderLeft: `3px solid ${selected?.id === chat.id ? '#00d55b' : 'transparent'}`, transition: 'background 0.15s' }}
                onMouseEnter={e => { if (selected?.id !== chat.id) e.currentTarget.style.background = '#0d0d0d'; }}
                onMouseLeave={e => { if (selected?.id !== chat.id) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#00d55b', flexShrink: 0 }}>
                    {chat.visitor_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chat.visitor_name || 'Visitor'}</span>
                      {chat.unread_count > 0 && <span style={{ background: '#00d55b', color: '#000', fontSize: 9, padding: '1px 6px', borderRadius: 10, fontWeight: 700, flexShrink: 0, marginLeft: 4 }}>{chat.unread_count}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{chat.last_message || 'No messages yet'}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: '#444' }}>{chat.city || ''}{chat.country ? `, ${chat.country}` : ''}</span>
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: STATUS_COLOR[chat.status]?.bg || '#1a1a1a', color: STATUS_COLOR[chat.status]?.text || '#555' }}>{chat.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: chat detail */}
        {selected ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Chat header */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #1e1e1e', background: '#0d0d0d', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#00d55b', flexShrink: 0 }}>
                {selected.visitor_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{selected.visitor_name || 'Visitor'}</div>
                <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{selected.visitor_email} · {selected.visitor_phone}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {selected.status !== 'resolved' && (
                  <button onClick={() => resolveChat(selected.id)} style={{ padding: '6px 14px', background: 'rgba(0,213,91,0.1)', border: '1px solid #00d55b', color: '#00d55b', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✓ Resolve</button>
                )}
              </div>
            </div>

            {/* Visitor info bar */}
            <div style={{ padding: '8px 20px', background: '#0a0a0a', borderBottom: '1px solid #1e1e1e', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {[
                ['📍', `${selected.city || ''}${selected.country ? `, ${selected.country}` : ''}`],
                ['📱', selected.device_type],
                ['🌐', selected.current_page],
                ['🔗', selected.agent_name ? `Agent: ${selected.agent_name}` : 'No agent'],
                ['⭐', selected.rating ? `${selected.rating}/5` : 'No rating']
              ].filter(([, v]) => v && v !== 'undefined').map(([icon, val]) => (
                <span key={val} style={{ fontSize: 11, color: '#555', display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span>{icon}</span><span>{val}</span>
                </span>
              ))}
              <span style={{ fontSize: 11, color: '#555', marginLeft: 'auto' }}>
                {new Date(selected.started_at).toLocaleString()}
              </span>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #1e1e1e', background: '#0d0d0d' }}>
              {['messages', 'visitor info'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  style={{ padding: '10px 20px', background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === t ? '#00d55b' : 'transparent'}`, color: tab === t ? '#00d55b' : '#555', fontSize: 13, fontWeight: tab === t ? 600 : 400, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {t}
                </button>
              ))}
            </div>

            {/* Messages tab */}
            {tab === 'messages' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#333', fontSize: 13, padding: 32 }}>No messages in this chat</div>
                ) : messages.map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: m.sender_type === 'agent' ? 'flex-end' : m.sender_type === 'system' ? 'center' : 'flex-start' }}>
                    {m.sender_type === 'system' ? (
                      <span style={{ fontSize: 11, color: '#333', background: '#111', padding: '3px 12px', borderRadius: 20, border: '1px solid #1e1e1e' }}>{m.message_text}</span>
                    ) : (
                      <div style={{ maxWidth: '68%' }}>
                        <div style={{ padding: '9px 13px', borderRadius: 12, background: m.sender_type === 'agent' ? '#00d55b' : '#1a1a1a', color: m.sender_type === 'agent' ? '#000' : '#fff', fontSize: 13, lineHeight: 1.55, borderBottomRightRadius: m.sender_type === 'agent' ? 2 : 12, borderBottomLeftRadius: m.sender_type === 'visitor' ? 2 : 12, fontWeight: m.sender_type === 'agent' ? 500 : 400 }}>
                          {m.message_text}
                        </div>
                        <div style={{ fontSize: 10, color: '#333', marginTop: 3, textAlign: m.sender_type === 'agent' ? 'right' : 'left' }}>
                          {m.sender_type === 'agent' ? (m.agent_name || 'Agent') : m.sender_type === 'ai' ? 'AI' : 'Visitor'} · {new Date(m.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Rating */}
                {selected.rating && (
                  <div style={{ marginTop: 16, background: '#111', border: '1px solid #1e1e1e', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Visitor rating</div>
                      <div style={{ fontSize: 18, color: '#f59e0b' }}>{'★'.repeat(selected.rating)}{'☆'.repeat(5 - selected.rating)}</div>
                    </div>
                    {selected.rating_comment && <div style={{ fontSize: 13, color: '#888', fontStyle: 'italic' }}>"{selected.rating_comment}"</div>}
                  </div>
                )}
              </div>
            )}

            {/* Visitor info tab */}
            {tab === 'visitor info' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    ['Name', selected.visitor_name],
                    ['Email', selected.visitor_email],
                    ['Phone', selected.visitor_phone],
                    ['Country', selected.country],
                    ['City', selected.city],
                    ['Device', selected.device_type],
                    ['Current Page', selected.current_page],
                    ['Chat Started', selected.started_at ? new Date(selected.started_at).toLocaleString() : '—'],
                    ['Chat Status', selected.status],
                    ['Handled by', selected.agent_name || 'No agent'],
                    ['Response Time', selected.response_time_seconds ? `${selected.response_time_seconds}s` : '—'],
                    ['Rating', selected.rating ? `${selected.rating}/5` : 'No rating']
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>{k}</div>
                      <div style={{ fontSize: 13, color: '#fff', wordBreak: 'break-all' }}>{v || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 48 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: '#555' }}>Select a chat to view</div>
            <div style={{ fontSize: 13, color: '#333' }}>Click any conversation on the left</div>
          </div>
        )}
      </div>
    </DashLayout>
  );
}